// specialOrderApproval.js
//
// Captain-side approval of special orders: the KOT rows a customer has placed
// and confirmed, waiting for the captain to accept them.
//
//   KOT / CONFIRMED BY CUSTOMER  ->  KOT / CONFIRMED
//
// STATUS IDS ARE RESOLVED BY NAME, NOT HARDCODED, and that is load-bearing.
// The live GetresActionStatusflow for this company reads:
//
//     id=51  statusid=15   seq=0  CONFIRMED BY CUSTOMER
//     id=48  statusid=10   seq=1  CONFIRMED
//     id= 1  statusid=11   seq=2  SENT TO KOT
//     id= 2  statusid=12   seq=3  ACCEPTED
//     id= 3  statusid=13   seq=4  PREPARING
//     id= 4  statusid=14   seq=5  READY
//     id= 5  statusid=1999 seq=6  CLOSED
//
// Note 15 and 10: CONFIRMED BY CUSTOMER is 15 and CONFIRMED is 10, which is
// the reverse of what kotActions.js still assumes (its CONFIRMED_STATUS_ID=10
// is commented "CONFIRMED BY CUSTOMER"). The backend renumbered these — the
// same renumbering SpecialOrderDetails.fetchResStatusMaster warns about, where
// CLOSED went 15 -> 1999. Names are stable, numbers are not, so everything
// here looks up by name and only falls back to the numbers above if the master
// cannot be read.

// Approving an order moves TWO things, because the two ladders track
// different questions — where each dish is, and where the order as a whole is:
//
//   KOT   (actiontype 1)  CONFIRMED BY CUSTOMER (15) -> CONFIRMED (10)
//   ORDER (actiontype 0)  CONFIRMED (2)              -> APPROVED  (3)
//
// Every pending special order in the live data carries exactly one ORDER row
// (actionnum 1) sitting at CONFIRMED, which is what SpecialOrderDetails writes
// when the customer places it. APPROVED is the next rung: NEW(1) ->
// CONFIRMED(2) -> APPROVED(3) -> CLOSED(999).
// Rejecting moves the same two ladders the other way:
//
//   KOT   (actiontype 1)  CONFIRMED BY CUSTOMER (15) -> CANCELLED (17)
//   ORDER (actiontype 0)  CONFIRMED (2)              -> REJECTED  (4)
//
// The KOT ladder has no REJECTED of its own — CANCELLED is its only terminal
// besides CLOSED, so that is where a refused line lands.
export const KOT_ACTION_NAME = 'KOT';
export const ORDER_ACTION_NAME = 'ORDER';

export const STATUS_CONFIRMED_BY_CUSTOMER = 'CONFIRMED BY CUSTOMER';
export const STATUS_NEW = 'NEW';
export const STATUS_CONFIRMED = 'CONFIRMED';
export const STATUS_APPROVED = 'APPROVED';
export const STATUS_CANCELLED = 'CANCELLED';
export const STATUS_REJECTED = 'REJECTED';

// Last-resort values, matching the live master at the time of writing. Only
// used when GetresActionStatusflow could not be read at all.
export const FALLBACK_STATUS_IDS = {
    confirmedByCustomer: 15,
    confirmed: 10,
    kotCancelled: 17,
    orderNew: 1,
    orderConfirmed: 2,
    orderApproved: 3,
    orderRejected: 4,
};

// GetresActionAllList calls the KOT action `actiontype` 1 and the order-level
// action 0. Rows for other actions (BILL 4, PACKING 2, PAYMENT 5...) share the
// endpoint and must not be mistaken for menu items.
export const KOT_ACTION_TYPE = 1;
export const ORDER_ACTION_TYPE = 0;

const same = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();

// GetresActionStatusflow rows are camelCase: { id, actionid, actionname,
// statusid, statusName, sequence, isActive }.
export const resolveStatusId = (statusMaster, actionName, statusName, fallback) => {
    const row = (statusMaster || []).find(
        (r) => r.isActive !== false && same(r.actionname, actionName) && same(r.statusName, statusName)
    );
    return row ? Number(row.statusid) : fallback;
};

export const getDecisionStatusIds = (statusMaster) => ({
    confirmedByCustomer: resolveStatusId(
        statusMaster, KOT_ACTION_NAME, STATUS_CONFIRMED_BY_CUSTOMER,
        FALLBACK_STATUS_IDS.confirmedByCustomer,
    ),
    confirmed: resolveStatusId(
        statusMaster, KOT_ACTION_NAME, STATUS_CONFIRMED,
        FALLBACK_STATUS_IDS.confirmed,
    ),
    kotCancelled: resolveStatusId(
        statusMaster, KOT_ACTION_NAME, STATUS_CANCELLED,
        FALLBACK_STATUS_IDS.kotCancelled,
    ),
    // A QR order's order row sits at ORDER/NEW: the QR page raises it when the
    // table is claimed. A special order's sits at ORDER/CONFIRMED. That is the
    // one thing that tells the two apart while their lines share
    // CONFIRMED BY CUSTOMER.
    orderNew: resolveStatusId(
        statusMaster, ORDER_ACTION_NAME, STATUS_NEW,
        FALLBACK_STATUS_IDS.orderNew,
    ),
    // ORDER/CONFIRMED and KOT/CONFIRMED share a name but not a number (2 vs
    // 10), which is exactly why resolveStatusId scopes by action name too.
    orderConfirmed: resolveStatusId(
        statusMaster, ORDER_ACTION_NAME, STATUS_CONFIRMED,
        FALLBACK_STATUS_IDS.orderConfirmed,
    ),
    orderApproved: resolveStatusId(
        statusMaster, ORDER_ACTION_NAME, STATUS_APPROVED,
        FALLBACK_STATUS_IDS.orderApproved,
    ),
    orderRejected: resolveStatusId(
        statusMaster, ORDER_ACTION_NAME, STATUS_REJECTED,
        FALLBACK_STATUS_IDS.orderRejected,
    ),
});

const isBlank = (v) => v === undefined || v === null || v === '';

// Casing is not consistent across this endpoint's columns, so each read tries
// the spellings actually seen in the response.
const firstOf = (row, keys) => {
    for (const k of keys) {
        if (!isBlank(row[k])) return row[k];
    }
    return null;
};

const menuNameFor = (menuItems, menuId) => {
    const menu = (menuItems || []).find((x) =>
        String(x.value) === String(menuId) ||
        String(x.id) === String(menuId) ||
        String(x.menuId) === String(menuId)
    );
    if (!menu) return `Menu ${menuId}`;
    return menu.label || menu.name || menu.menuName || menu.text || `Menu ${menuId}`;
};

// Every KOT row still sitting at CONFIRMED BY CUSTOMER, grouped into one entry
// per order (requestid) — the captain approves an order, not a line.
//
// Sorted by delivery date ascending: these are future-dated by nature (the
// live queue runs from today out to two weeks), so the soonest is the one that
// actually needs a decision.
// qrOrderStatusId: orders whose order row is at this status (ORDER/NEW) are QR
// orders, which the Captain Dashboard lists under QR Orders instead — they are
// left out here.
export const groupPendingSpecialOrders = (actionRows, menuItems, confirmedByCustomerId, qrOrderStatusId) => {
    const wanted = Number(confirmedByCustomerId);
    const groups = new Map();

    // The order-level row per request, picked up in the same pass so approving
    // can advance it alongside the lines. Lowest actionnum wins if a request
    // ever carries more than one; today every pending order has exactly one.
    const orderRows = new Map();
    (actionRows || []).forEach((row) => {
        if (Number(row.actiontype) !== ORDER_ACTION_TYPE) return;
        if (isBlank(row.requestid)) return;
        const held = orderRows.get(row.requestid);
        if (!held || Number(row.actionnum) < Number(held.actionnum)) {
            orderRows.set(row.requestid, row);
        }
    });

    (actionRows || []).forEach((row) => {
        if (Number(row.actiontype) !== KOT_ACTION_TYPE) return;
        if (Number(row.statusId) !== wanted) return;
        if (isBlank(row.requestid)) return;
        const orderRow = orderRows.get(row.requestid);
        if (orderRow && Number(orderRow.statusId) === Number(qrOrderStatusId)) return;
        // A KOT row with no menu id is not a line item.
        if (isBlank(row.menuId)) return;

        const requestId = row.requestid;
        if (!groups.has(requestId)) {
            groups.set(requestId, {
                requestId,
                orderNo: firstOf(row, ['ptsjrsr']) || requestId,
                kotNumber: firstOf(row, ['kotnumber']),
                customerName: firstOf(row, ['customername']),
                phone: firstOf(row, ['customermobilnumber']),
                address: firstOf(row, ['deliveryaddress']),
                deliveryDate: firstOf(row, ['deliverydate', 'deliveryDate', 'DeliveryDate']),
                deliveryTime: firstOf(row, ['expectedDeliveryTime']),
                orderType: firstOf(row, ['ordertype']),
                tableNo: firstOf(row, ['tablenumber']),
                placedOn: firstOf(row, ['targetdate', 'createddate']),
                // May be absent; approve then updates the lines only and says
                // so, rather than silently doing half the job.
                orderRow: orderRows.get(requestId) || null,
                items: [],
                total: 0,
            });
        }

        const group = groups.get(requestId);
        const qty = Number(row.menuQty) || 0;
        const rate = Number(row.menuitemrate) || 0;
        // menuitemnetamount is 0 on older rows (nothing wrote it until
        // recently), so the line value falls back to qty x rate.
        const net = Number(row.menuitemnetamount) || qty * rate;

        group.items.push({
            id: row.requestactionid,
            name: menuNameFor(menuItems, row.menuId),
            qty,
            rate,
            net,
            instruction: firstOf(row, ['menuitemspecialinstruction']),
            // The untouched backend row. The approve payload is built from
            // this rather than from a stripped-down copy of the display shape,
            // so no display field can leak into the write.
            raw: row,
        });
        group.total += net;
    });

    return [...groups.values()].sort((a, b) => {
        const ta = a.deliveryDate ? new Date(a.deliveryDate).getTime() : Infinity;
        const tb = b.deliveryDate ? new Date(b.deliveryDate).getTime() : Infinity;
        if (ta !== tb) return ta - tb;
        return String(a.orderNo).localeCompare(String(b.orderNo));
    });
};

// Restaurant_Action_Update payload for deciding one whole order: every KOT
// line to `lineStatusId`, plus the order-level row to `orderStatusId`.
//
// Each row is posted back whole with only statusId changed, and each keeps its
// OWN actiontype — writing the KOT action type onto the order row would file
// the order as a menu line. `raw` was never mixed with display fields, so
// there is nothing to strip out first (unlike kotActions' destructuring).
const buildDecisionPayload = (group, lineStatusId, orderStatusId) => {
    if (!group) return [];

    const lines = (group.items || []).map((item) => ({
        ...item.raw,
        actiontype: KOT_ACTION_TYPE,
        statusId: Number(lineStatusId),
    }));

    if (!group.orderRow) return lines;

    return [
        ...lines,
        {
            ...group.orderRow,
            actiontype: ORDER_ACTION_TYPE,
            statusId: Number(orderStatusId),
        },
    ];
};

// KOT lines -> CONFIRMED, order row -> APPROVED.
export const buildApprovePayload = (group, statusIds) =>
    buildDecisionPayload(group, statusIds.confirmed, statusIds.orderApproved);

// KOT lines -> CANCELLED, order row -> REJECTED.
export const buildRejectPayload = (group, statusIds) =>
    buildDecisionPayload(group, statusIds.kotCancelled, statusIds.orderRejected);
